import React, { useEffect, useState } from "react";
import { Button, Space, Tag, Popconfirm, Select, Flex, Typography, Modal, Image } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarcodeOutlined, PrinterOutlined, HistoryOutlined, PictureOutlined, FileAddOutlined, ScanOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useNotification } from 'NotificationContext';
import * as actions from "store/actions/AuthActions";
import dayjs from 'dayjs';
import BarcodeSheetGenerator from "components/Assets/BarcodeSheetGenerator";
import BarcodeScanner from "components/Assets/BarcodeScanner";
import AssetEntryScanner from "components/Assets/AssetEntryScanner";
import FilterBar, { FilterField } from "components/Common/FilterBar";
import PageActions from "components/Common/PageActions";
import useUrlFilters from "hooks/useUrlFilters";
const { Option } = Select;

// Search, status and paging live in the URL so a filtered list can be shared as a link.
const URL_FILTERS = {
    search: { type: 'string' },
    status: { type: 'string' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const Assets = () => {
    const navigate = useNavigate();
    const notify = useNotification();

    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useUrlFilters(URL_FILTERS);
    const [total, setTotal] = useState(0);
    const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
    const pagination = { current: filters.page, pageSize, total };
    const searchText = filters.search || '';
    const statusFilter = filters.status || null;
    const [searchLive, setSearchLive] = useState(searchText);
    // Keep the box in step when the URL changes underneath it (back/forward).
    useEffect(() => setSearchLive(searchText), [searchText]);
    const [, setCategories] = useState([]);
    const [, setLocations] = useState([]);
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [selectedAssetHistory, setSelectedAssetHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [barcodeSheetModalVisible, setBarcodeSheetModalVisible] = useState(false);
    const [scannerModalVisible, setScannerModalVisible] = useState(false);
    const [entryScannerModalVisible, setEntryScannerModalVisible] = useState(false);

    const statusColors = {
        AVAILABLE: 'green',
        ASSIGNED: 'blue',
        IN_MAINTENANCE: 'orange',
        RETIRED: 'gray',
        LOST: 'red',
        DAMAGED: 'volcano'
    };

    const loadAssets = async (page = 1, search = '', status = null) => {
        setLoading(true);
        try {
            const response = await actions.loadAssets(page, search, pageSize, status);
            if (response && response.status === 200) {
                setAssets(response.data.results || []);
                setTotal(response.data.count || 0);
            }
        } catch (error) {
            notify("error", "Failed to load assets");
        }
        setLoading(false);
    };

    const loadCategories = async () => {
        try {
            const response = await actions.loadAssetCategories();
            if (response && response.status === 200) {
                setCategories(response.data.results || response.data || []);
            }
        } catch (error) {
            console.error("Failed to load categories", error);
        }
    };

    const loadLocations = async () => {
        try {
            const response = await actions.loadAssetLocations();
            if (response && response.status === 200) {
                setLocations(response.data.results || response.data || []);
            }
        } catch (error) {
            console.error("Failed to load locations", error);
        }
    };

    useEffect(() => {
        loadCategories();
        loadLocations();
    }, []);

    useEffect(() => {
        loadAssets(filters.page, searchText, statusFilter);
    }, [filters.page, pageSize, searchText, statusFilter]);

    const handleSearch = (value) => {
        setFilters({ search: value, page: 1 });
    };

    const handleStatusChange = (value) => {
        setFilters({ status: value, page: 1 });
    };

    const handleTableChange = (paginationInfo) => {
        const sizeChanged = paginationInfo.pageSize !== pageSize;
        setFilters({ page: sizeChanged ? 1 : paginationInfo.current, pageSize: paginationInfo.pageSize });
    };

    const handleDelete = async (id) => {
        try {
            const response = await actions.deleteAsset(id);
            if (response) {
                notify("success", "Asset deleted successfully");
                loadAssets(pagination.current, searchText, statusFilter);
            }
        } catch (error) {
            notify("error", "Failed to delete asset");
        }
    };

    const handleGenerateBarcode = async (id) => {
        try {
            const response = await actions.generateAssetBarcode(id);
            if (response && response.status === 200) {
                notify("success", response.data.message || "Barcode generated successfully");
                loadAssets(pagination.current, searchText, statusFilter);
            }
        } catch (error) {
            notify("error", "Failed to generate barcode");
        }
    };

    const handlePrintBarcode = async (asset) => {
        if (!asset.barcode_image_url && !asset.qrcode_image_url) {
            notify("warning", "Please generate barcode & QR code first");
            return;
        }
        // Open print window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Barcode & QR Code - ${asset.asset_number}</title>
                <style>
                    @media print {
                        @page { size: 80mm 50mm; margin: 2mm; }
                        body { margin: 0; padding: 0; }
                        .no-print { display: none !important; }
                    }
                    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
                    .label-container {
                        width: 76mm; height: 46mm; padding: 2mm;
                        border: 1px solid #ccc;
                        background: white;
                    }
                    .company-name { font-size: 10px; font-weight: bold; text-align: center; margin-bottom: 2mm; }
                    .codes-row {
                        display: flex; align-items: center;
                        justify-content: space-between; gap: 3mm;
                    }
                    .barcode-section { flex: 1; text-align: center; }
                    .qrcode-section { width: 22mm; text-align: center; }
                    .barcode-img { max-width: 48mm; height: auto; }
                    .qrcode-img { width: 20mm; height: 20mm; }
                    .asset-info { font-size: 8px; text-align: center; margin-top: 2mm; color: #666; }
                    .print-btn {
                        display: block; margin: 20px auto;
                        padding: 10px 30px; font-size: 16px;
                        background: #28a745; color: white;
                        border: none; border-radius: 5px; cursor: pointer;
                    }
                </style>
            </head>
            <body>
                <button class="print-btn no-print" onclick="window.print()">Print Barcode & QR Code</button>
                <div class="label-container">
                    <div class="company-name">TESON LLP</div>
                    <div class="codes-row">
                        <div class="barcode-section">
                            ${asset.barcode_image_url ? `<img src="${asset.barcode_image_url}" class="barcode-img" alt="Barcode" />` : '<p>No barcode</p>'}
                        </div>
                        <div class="qrcode-section">
                            ${asset.qrcode_image_url ? `<img src="${asset.qrcode_image_url}" class="qrcode-img" alt="QR Code" />` : '<p>No QR</p>'}
                        </div>
                    </div>
                    <div class="asset-info">${asset.name} | ${asset.asset_number}</div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleViewHistory = async (assetId) => {
        setHistoryLoading(true);
        setHistoryModalVisible(true);
        try {
            const response = await actions.loadAssetHistory(assetId);
            if (response && response.status === 200) {
                setSelectedAssetHistory(response.data || []);
            }
        } catch (error) {
            notify("error", "Failed to load asset history");
        }
        setHistoryLoading(false);
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'primary_image_url',
            key: 'primary_image_url',
            width: 70,
            render: (url) => url ? (
                <Image
                    src={url}
                    width={50}
                    height={50}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    preview={{ mask: null }}
                />
            ) : (
                <div style={{
                    width: 50,
                    height: 50,
                    background: '#f5f5f5',
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <PictureOutlined style={{ color: '#bfbfbf', fontSize: 20 }} />
                </div>
            )
        },
        {
            title: 'Asset Number',
            dataIndex: 'asset_number',
            key: 'asset_number',
            sorter: true,
            render: (text, record) => (
                <a onClick={() => navigate(`/assets/edit/${record.id}`)}>{text}</a>
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
        },
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            key: 'barcode',
            render: (text) => text || <span style={{ color: '#999' }}>Not generated</span>
        },
        {
            title: 'Category',
            dataIndex: 'category_name',
            key: 'category_name',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <Tag color={statusColors[status] || 'default'}>
                    {record.status_display}
                </Tag>
            )
        },
        {
            title: 'Assigned To',
            dataIndex: 'assigned_to_name',
            key: 'assigned_to_name',
            render: (text) => text || '-'
        },
        {
            title: 'Location',
            dataIndex: 'location_name',
            key: 'location_name',
            render: (text) => text || '-'
        },
        {
            title: 'Serial Number',
            dataIndex: 'serial_number',
            key: 'serial_number',
            render: (text) => text || '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<BarcodeOutlined />}
                        onClick={() => handleGenerateBarcode(record.id)}
                        title="Generate Barcode & QR Code"
                    />
                    {(record.barcode_image_url || record.qrcode_image_url) && (
                        <Button
                            type="link"
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrintBarcode(record)}
                            title="Print Barcode & QR Code"
                        />
                    )}
                    <Button
                        type="link"
                        icon={<HistoryOutlined />}
                        onClick={() => handleViewHistory(record.id)}
                        title="View History"
                    />
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/assets/edit/${record.id}`)}
                        title="Edit"
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this asset?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} title="Delete" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const historyColumns = [
        {
            title: 'Date',
            dataIndex: 'history_date',
            key: 'history_date',
            render: (text) => dayjs(text).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Status',
            dataIndex: 'status_display',
            key: 'status_display',
        },
        {
            title: 'Assigned To',
            dataIndex: 'assigned_to_name',
            key: 'assigned_to_name',
            render: (text) => text || '-'
        },
        {
            title: 'Location',
            dataIndex: 'location_name',
            key: 'location_name',
            render: (text) => text || '-'
        },
        {
            title: 'Modified By',
            dataIndex: 'history_user',
            key: 'history_user',
        }
    ];

    return (
        <div className='custom-scroll-div' style={{ overflowY: "scroll", maxHeight: "90vh", padding: 10, paddingBottom: 30 }}>
            <Flex justify='space-between' align='center' style={{ marginBottom: 16 }}>
                <Typography.Title level={3}>Assets</Typography.Title>
                <PageActions
                    primary={{
                        label: 'Add Asset',
                        shortLabel: 'Add',
                        icon: <PlusOutlined />,
                        onClick: () => navigate('/assets/new'),
                    }}
                    actions={[
                        {
                            key: 'entry-scan',
                            label: 'Asset Entry (Scan)',
                            icon: <PlusCircleOutlined />,
                            onClick: () => setEntryScannerModalVisible(true),
                        },
                        {
                            key: 'scanner',
                            label: 'Barcode Scanner',
                            icon: <ScanOutlined />,
                            onClick: () => setScannerModalVisible(true),
                        },
                        {
                            key: 'barcode-sheet',
                            label: 'Generate Barcode Sheet',
                            icon: <FileAddOutlined />,
                            onClick: () => setBarcodeSheetModalVisible(true),
                        },
                    ]}
                />
            </Flex>

            <FilterBar
                searchValue={searchLive}
                onSearchChange={setSearchLive}
                onSearch={handleSearch}
                searchPlaceholder="Search by name, barcode, serial number"
                activeCount={statusFilter ? 1 : 0}
                onClear={() => handleStatusChange(null)}
                title="Filter Assets"
            >
                <FilterField label="Status">
                    <Select
                        placeholder="Filter by status"
                        allowClear
                        value={statusFilter}
                        onChange={handleStatusChange}
                    >
                        <Option value="AVAILABLE">Available</Option>
                        <Option value="ASSIGNED">Assigned</Option>
                        <Option value="IN_MAINTENANCE">In Maintenance</Option>
                        <Option value="RETIRED">Retired</Option>
                        <Option value="LOST">Lost</Option>
                        <Option value="DAMAGED">Damaged</Option>
                    </Select>
                </FilterField>
            </FilterBar>

            <Table
                columns={columns}
                dataSource={assets}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                }}
                onChange={handleTableChange}
                scroll={{ x: 1200 }}
            />

            <Modal
                title="Asset History"
                open={historyModalVisible}
                onCancel={() => setHistoryModalVisible(false)}
                footer={null}
                width={800}
            >
                <Table
                    columns={historyColumns}
                    dataSource={selectedAssetHistory}
                    rowKey="history_id"
                    loading={historyLoading}
                    pagination={false}
                    size="small"
                />
            </Modal>

            <BarcodeSheetGenerator
                visible={barcodeSheetModalVisible}
                onClose={() => setBarcodeSheetModalVisible(false)}
            />

            <BarcodeScanner
                visible={scannerModalVisible}
                onClose={() => setScannerModalVisible(false)}
            />

            <AssetEntryScanner
                visible={entryScannerModalVisible}
                onClose={() => setEntryScannerModalVisible(false)}
            />
        </div>
    );
};

export default Assets;
